import React, { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Breadcrumb,
  Tabs,
  message,
  theme,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  MinusOutlined,
  FileTextOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { SourceChapter, ChapterStatusMap, AdminTaskType } from '../../types';
import { sourceStoriesApi } from '../../services/api';
import { useApi, useAsyncOperation, useIsMobile } from '../../hooks';
import { formatDate, truncateText } from '../../utils';
import OverviewTab from './OverviewTab';
import ProcessTab from './ProcessTab';
import CharactersTab from './CharactersTab';
import GlossaryTab from './GlossaryTab';
import EpisodesTab from './EpisodesTab';
import SourceChapterModal from './SourceChapterModal';
import ChapterProcessDrawer from './ChapterProcessDrawer';
import ChapterSuggestionsDrawer from './ChapterSuggestionsDrawer';
import AutoPipelineConfigModal from '../../components/admin/tasks/AutoPipelineConfigModal';
import AutoChapterConfigModal from '../../components/admin/tasks/AutoChapterConfigModal';
import AutoProcessConfigModal from '../../components/admin/tasks/AutoProcessConfigModal';
import { RocketOutlined, RobotOutlined, SettingOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

enum ChapterVideoState {
  FULL_AUTO_PENDING = 'FULL_AUTO_PENDING',
  HAS_VIDEO = 'HAS_VIDEO',
  HAS_EPISODE = 'HAS_EPISODE',
  EPISODE_TASK_PENDING = 'EPISODE_TASK_PENDING',
  HAS_PROCESS = 'HAS_PROCESS',
  TASK_PENDING = 'TASK_PENDING',
  NO_PROCESS = 'NO_PROCESS',
}

const deriveChapterVideoState = (chapterStatus: any, videoForm: 'LONG' | 'SHORT'): ChapterVideoState => {
  if (!chapterStatus) return ChapterVideoState.NO_PROCESS;

  const { processes, activeTasks } = chapterStatus;

  // 1. FULL_AUTO_CHAPTER_TO_VIDEO task
  if (activeTasks?.some((t: any) => t.type === AdminTaskType.FULL_AUTO_CHAPTER_TO_VIDEO)) {
    return ChapterVideoState.FULL_AUTO_PENDING;
  }

  const formProcesses = processes?.filter((p: any) => p.videoForm === videoForm) || [];

  // 2. Has Video
  if (formProcesses.some((p: any) => p.hasVideo)) {
    return ChapterVideoState.HAS_VIDEO;
  }

  // 3. Has Episode
  if (formProcesses.some((p: any) => p.hasEpisode)) {
    return ChapterVideoState.HAS_EPISODE;
  }

  // 4. AUTO_EPISODE task for this form
  if (activeTasks?.some((t: any) => {
    if (t.type !== AdminTaskType.AUTO_EPISODE) return false;
    // For AUTO_EPISODE, we should also check videoForm if available
    if (t.videoForm && t.videoForm !== videoForm) return false;
    return true;
  })) {
    // Note: in backend we already filtered activeTasks to only those relevant to this chapter.
    if (formProcesses.length > 0) {
      return ChapterVideoState.EPISODE_TASK_PENDING;
    }
  }

  // 5. Has Process
  if (formProcesses.length > 0) {
    return ChapterVideoState.HAS_PROCESS;
  }

  // 6. AUTO_PROCESS or AUTO_CHAPTER task for this form
  if (activeTasks?.some((t: any) => {
    // If task has videoForm, it must match. If not (e.g. old tasks or AUTO_CHAPTER), 
    // we might have to assume it's for both or default to LONG.
    // For AUTO_CHAPTER, it usually creates chapters then processes, so it's relevant.
    if (t.videoForm && t.videoForm !== videoForm) return false;
    return t.type === AdminTaskType.AUTO_PROCESS || t.type === AdminTaskType.AUTO_CHAPTER;
  })) {
    return ChapterVideoState.TASK_PENDING;
  }

  // 7. No process
  return ChapterVideoState.NO_PROCESS;
};

const getStatusUI = (state: ChapterVideoState) => {
  switch (state) {
    case ChapterVideoState.HAS_VIDEO:
      return { color: '#52c41a', icon: <CheckCircleOutlined />, label: 'Video' };
    case ChapterVideoState.HAS_EPISODE:
      return { color: '#1890ff', icon: <FileTextOutlined />, label: 'Episode' };
    case ChapterVideoState.EPISODE_TASK_PENDING:
    case ChapterVideoState.TASK_PENDING:
    case ChapterVideoState.FULL_AUTO_PENDING:
      return { color: '#fa8c16', icon: <SyncOutlined spin />, label: 'Pending' };
    case ChapterVideoState.HAS_PROCESS:
      return { color: '#fadb14', icon: <SettingOutlined />, label: 'Process' };
    case ChapterVideoState.NO_PROCESS:
    default:
      return { color: '#d9d9d9', icon: <MinusOutlined />, label: 'None' };
  }
};

const SourceStoryDetail: React.FC = () => {
  const { id: storyId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const { token: { colorBorder } } = theme.useToken();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAutoModalVisible, setIsAutoModalVisible] = useState(false);
  const [isAutoChapterModalVisible, setIsAutoChapterModalVisible] = useState(false);
  const [isAutoProcessModalVisible, setIsAutoProcessModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingChapter, setEditingChapter] = useState<SourceChapter | null>(null);
  const [viewingChapter, setViewingChapter] = useState<SourceChapter | null>(null);
  const [autoProcessChapter, setAutoProcessChapter] = useState<SourceChapter | null>(null);
  const [selectedChapterForDrawer, setSelectedChapterForDrawer] = useState<SourceChapter | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isSuggestionsDrawerVisible, setIsSuggestionsDrawerVisible] = useState(false);
  const [chapterStatusMap, setChapterStatusMap] = useState<ChapterStatusMap>({});
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const { execute: executeAsync } = useAsyncOperation();
  const isMobile = useIsMobile();

  // Fetch story detail
  const {
    data: storyData,
    loading: storyLoading,
    refresh: refreshStory,
  } = useApi(
    useCallback(() => {
      if (!storyId) return Promise.reject(new Error('Story ID is required'));
      return sourceStoriesApi.getSourceStoryDetail(storyId);
    }, [storyId]),
    { immediate: true }
  );

  // Fetch chapters
  const getChapters = useCallback(() => {
    if (!storyId) {
      return Promise.resolve({ data: [], totalRecord: 0 });
    }
    return sourceStoriesApi.getSourceChapters(storyId, {
      pageIndex: 0,
      pageSize: 500,
    });
  }, [storyId]);

  const {
    data: chaptersData,
    loading: chaptersLoading,
    refresh: refreshChapters,
  } = useApi(getChapters, { immediate: true });

  const fetchChapterStatuses = useCallback(async () => {
    if (!storyId || !chaptersData?.data || chaptersData.data.length === 0) return;
    
    setIsStatusLoading(true);
    try {
      const chapterIds = chaptersData.data.map(c => c.id);
      const res = await sourceStoriesApi.getChapterStatuses(storyId, chapterIds);
      setChapterStatusMap(res.data);
    } catch (err) {
      console.error('Failed to fetch chapter statuses', err);
    } finally {
      setIsStatusLoading(false);
    }
  }, [storyId, chaptersData?.data]);

  useEffect(() => {
    fetchChapterStatuses();
  }, [fetchChapterStatuses]);

  const handleRefreshAll = useCallback(() => {
    refreshChapters();
    fetchChapterStatuses();
  }, [refreshChapters, fetchChapterStatuses]);

  const handleCreate = () => {
    setEditingChapter(null);
    setIsModalVisible(true);
  };

  const handleEdit = (chapter: SourceChapter) => {
    setEditingChapter(chapter);
    setIsModalVisible(true);
  };

  const handleView = (chapter: SourceChapter) => {
    setViewingChapter(chapter);
    setIsViewModalVisible(true);
  };

  const handleGenerateSummary = async (chapter: SourceChapter) => {
    if (!storyId) return;
    await executeAsync(
      () => sourceStoriesApi.generateChapterSummary(storyId, chapter.id),
      { successMessage: 'Summary generated successfully' }
    );
    refreshChapters();
  };

  const handleDelete = async (chapter: SourceChapter) => {
    if (!storyId) return;
    await executeAsync(
      () => sourceStoriesApi.deleteSourceChapter(storyId, chapter.id),
      { successMessage: 'Chapter deleted successfully!' }
    );
    refreshChapters();
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    handleRefreshAll();
  };

  const handleCopyContent = (content: string) => {
    navigator.clipboard.writeText(content);
    message.success('Content copied to clipboard!');
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 60,
      render: (val: number) => val,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => text || '-',
    },
    {
      title: 'Content Preview',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => text ? truncateText(text, 100) : '-',
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (link: string) => link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {truncateText(link, 30)}
        </a>
      ) : '-',
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      key: 'summary',
      render: (text: string) => text ? truncateText(text, 100) : '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_: any, record: SourceChapter) => {
        const status = chapterStatusMap[record.id];
        if (isStatusLoading && !status) return <Spin size="small" />;
        
        const longState = deriveChapterVideoState(status, 'LONG');
        const shortState = deriveChapterVideoState(status, 'SHORT');
        const longUI = getStatusUI(longState);
        const shortUI = getStatusUI(shortState);

        return (
          <Space direction="vertical" size={4}>
            <Space size={4}>
              <Tag 
                color={longUI.color} 
                icon={longUI.icon}
                style={{ cursor: 'pointer', margin: 0, minWidth: 80 }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setSelectedChapterForDrawer(record);
                  setIsDrawerVisible(true);
                }}
              >
                LONG
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>{longUI.label}</Text>
            </Space>
            <Space size={4}>
              <Tag 
                color={shortUI.color} 
                icon={shortUI.icon}
                style={{ cursor: 'pointer', margin: 0, minWidth: 80 }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setSelectedChapterForDrawer(record);
                  setIsDrawerVisible(true);
                }}
              >
                SHORT
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>{shortUI.label}</Text>
            </Space>
          </Space>
        );
      }
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: SourceChapter) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Content"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Button
            type="text"
            icon={<FileTextOutlined />}
            onClick={() => handleGenerateSummary(record)}
            title="Generate Summary"
            style={{ color: '#1890ff' }}
            disabled={!record.content}
          />
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => {
              setAutoProcessChapter(record);
              setIsAutoProcessModalVisible(true);
            }}
            title="Auto Process"
            style={{ color: '#fa8c16' }}
            disabled={!record.content}
          />
          <Popconfirm
            title="Are you sure you want to delete this chapter?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const story = storyData?.data?.story;
  const storyVoices = storyData?.data?.storyVoices;

  const ChapterCard = ({ chapter }: { chapter: SourceChapter }) => {
    const status = chapterStatusMap[chapter.id];
    const longState = deriveChapterVideoState(status, 'LONG');
    const shortState = deriveChapterVideoState(status, 'SHORT');
    const longUI = getStatusUI(longState);
    const shortUI = getStatusUI(shortState);

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderRadius: 8 }}
        bodyStyle={{ padding: 12 }}
        onClick={() => {
          setSelectedChapterForDrawer(chapter);
          setIsDrawerVisible(true);
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0, fontSize: 16 }}>
            #{chapter.orderIndex} {chapter.name || 'No Name'}
          </Title>
          <Space>
            <Tag color={longUI.color} style={{ margin: 0, fontSize: 10 }}>LONG</Tag>
            <Tag color={shortUI.color} style={{ margin: 0, fontSize: 10 }}>SHORT</Tag>
          </Space>
        </div>
        
        <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.65)', marginBottom: 8 }}>
          {chapter.content ? truncateText(chapter.content, 80) : <span style={{ fontStyle: 'italic' }}>No content</span>}
        </div>

        {chapter.summary && (
          <div style={{ fontSize: 13, color: '#1890ff', marginBottom: 8 }}>
            <strong>Summary:</strong> {truncateText(chapter.summary, 80)}
          </div>
        )}
  
        {chapter.link && (
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <a href={chapter.link} target="_blank" rel="noopener noreferrer" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {truncateText(chapter.link, 40)}
            </a>
          </div>
        )}
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: 12,
          paddingTop: 8,
          borderTop: `1px solid ${colorBorder}44`
        }}>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
            {formatDate(chapter.createdAt)}
          </div>
          <Space size={8}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleView(chapter);
              }}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleEdit(chapter);
              }}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleGenerateSummary(chapter);
              }}
              style={{ color: '#1890ff', backgroundColor: '#e6f7ff' }}
            />
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setAutoProcessChapter(chapter);
                setIsAutoProcessModalVisible(true);
              }}
              style={{ color: '#fa8c16', backgroundColor: '#fff7e6' }}
              disabled={!chapter.content}
            />
            <Popconfirm
              title="Delete this chapter?"
              onConfirm={() => handleDelete(chapter)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{ backgroundColor: '#fff1f0' }}
              />
            </Popconfirm>
          </Space>
        </div>
      </Card>
    );
  };

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: story ? (
        <OverviewTab 
          story={story} 
          storyVoices={storyVoices}
          totalChapters={chaptersData?.totalRecord || 0} 
          onRefreshStory={refreshStory}
          loading={storyLoading}
        />
      ) : (
        <Card loading={storyLoading} />
      ),
    },
    {
      key: 'chapters',
      label: 'Chapters',
      children: (
        <Card bodyStyle={isMobile ? { padding: '12px 8px' } : undefined}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            marginBottom: 16,
            gap: isMobile ? 12 : 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                Chapters
              </Title>
              <Button 
                type="default" 
                shape="circle"
                icon={<ReloadOutlined spin={chaptersLoading || isStatusLoading} />} 
                onClick={handleRefreshAll}
                title="Refresh Data"
                style={{ marginLeft: 8 }}
              />
            </div>
            <Space size={isMobile ? 8 : 16} wrap={isMobile}>
              <Button
                type="default"
                icon={<RobotOutlined />}
                onClick={() => setIsAutoChapterModalVisible(true)}
                style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Auto' : 'Auto Chapter'}
              </Button>
              <Button
                type="default"
                icon={<RocketOutlined />}
                onClick={() => setIsAutoModalVisible(true)}
                style={{ backgroundColor: '#f0f5ff', borderColor: '#adc6ff', color: '#1d39c4' }}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Full Pipeline' : 'Tự động Full Pipeline'}
              </Button>
              <Button
                type="default"
                icon={<BulbOutlined />}
                onClick={() => setIsSuggestionsDrawerVisible(true)}
                style={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f', color: '#d48806' }}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Gợi ý' : 'Gợi ý chủ đề'}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Add' : 'Add Chapter'}
              </Button>
            </Space>
          </div>

          {!isMobile ? (
            <Table
              columns={columns}
              dataSource={chaptersData?.data || []}
              rowKey="id"
              loading={chaptersLoading}
              pagination={false}
            />
          ) : (
            <div style={{ marginTop: 8 }}>
              {chaptersLoading ? (
                <Card loading />
              ) : (
                (chaptersData?.data || []).map(chapter => (
                  <ChapterCard key={chapter.id} chapter={chapter} />
                ))
              )}
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'characters',
      label: 'Characters',
      children: storyId ? <CharactersTab storyId={storyId} /> : null,
    },
    {
      key: 'glossary',
      label: 'Glossary',
      children: storyId ? <GlossaryTab storyId={storyId} /> : null,
    },
    {
      key: 'processes',
      label: 'Process',
      children: storyId ? (
        <ProcessTab 
          storyId={storyId} 
          chapters={chaptersData?.data || []} 
        />
      ) : null,
    },
    {
      key: 'episodes',
      label: 'Episodes',
      children: storyId ? <EpisodesTab storyId={storyId} /> : null,
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Breadcrumb.Item style={{ display: 'inline-flex', alignItems: 'center', padding: 0 }}>
          <Link
            to="/source-stories"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: 0,
              cursor: 'pointer',
              color: 'rgba(0,0,0,0.85)',
              textDecoration: 'none',
              fontSize: 14,
              lineHeight: 1
            }}
          >
            <ArrowLeftOutlined style={{ fontSize: 14, lineHeight: 1, transform: 'translateY(-1px)' }} />
            <span style={{ display: 'inline-block', lineHeight: 1, fontSize: 14 }}>Content Management</span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item style={{ display: 'inline-flex', alignItems: 'center', fontSize: 14 }}>
          {story?.name || 'Source Story Detail'}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange} 
        items={tabItems} 
      />

      {/* Create/Edit Chapter Modal */}
      {storyId && (
        <SourceChapterModal
          storyId={storyId}
          visible={isModalVisible}
          editingChapter={editingChapter}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Full Auto Pipeline Modal */}
      {storyId && story && (
        <AutoPipelineConfigModal
          storyId={storyId}
          storyName={story.name}
          visible={isAutoModalVisible}
          onCancel={() => setIsAutoModalVisible(false)}
          onSuccess={() => {
            setIsAutoModalVisible(false);
            // Có thể refresh gì đó ở đây nếu cần, tạm thời thông báo đã xong
          }}
        />
      )}

      {/* Auto Chapter Modal */}
      {storyId && story && (
        <AutoChapterConfigModal
          storyId={storyId}
          storyName={story.name}
          visible={isAutoChapterModalVisible}
          onCancel={() => setIsAutoChapterModalVisible(false)}
          onSuccess={() => {
            setIsAutoChapterModalVisible(false);
          }}
        />
      )}

      {/* Auto Process Modal */}
      {storyId && autoProcessChapter && (
        <AutoProcessConfigModal
          storyId={storyId}
          chapterId={autoProcessChapter.id}
          chapterName={autoProcessChapter.name || `Chương ${(autoProcessChapter.orderIndex ?? 0) + 1}`}
          visible={isAutoProcessModalVisible}
          onCancel={() => {
            setIsAutoProcessModalVisible(false);
            setAutoProcessChapter(null);
          }}
          onSuccess={() => {
            setIsAutoProcessModalVisible(false);
            setAutoProcessChapter(null);
          }}
        />
      )}

      {/* View Chapter Content Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
            <span>{`Chapter: ${viewingChapter?.name || `#${(viewingChapter?.orderIndex ?? 0) + 1}`}`}</span>
            {viewingChapter?.content && (
              <Button 
                type="text" 
                icon={<CopyOutlined />} 
                onClick={() => viewingChapter?.content && handleCopyContent(viewingChapter.content)}
                title="Copy content"
              >
                Copy
              </Button>
            )}
          </div>
        }
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {viewingChapter && (
          <div>
            {viewingChapter.link && (
              <div style={{ marginBottom: 16 }}>
                <strong>Link:</strong>{' '}
                <a href={viewingChapter.link} target="_blank" rel="noopener noreferrer">
                  {viewingChapter.link}
                </a>
              </div>
            )}
            <div
              style={{
                maxHeight: '60vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: 14,
                lineHeight: 1.6,
                padding: 16,
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
              }}
            >
              {viewingChapter.content || 'No content'}
            </div>
          </div>
        )}
      </Modal>

      <ChapterProcessDrawer
        visible={isDrawerVisible}
        onClose={() => {
          setIsDrawerVisible(false);
          setSelectedChapterForDrawer(null);
        }}
        chapter={selectedChapterForDrawer}
        chapterStatus={selectedChapterForDrawer ? chapterStatusMap[selectedChapterForDrawer.id] : null}
        loading={isStatusLoading}
      />

      {storyId && story && (
        <ChapterSuggestionsDrawer
          visible={isSuggestionsDrawerVisible}
          storyId={storyId}
          storyName={story.name}
          onClose={() => setIsSuggestionsDrawerVisible(false)}
        />
      )}
    </div>
  );
};

export default SourceStoryDetail;
